import { useState, useEffect } from 'react';
import { adminKiAbrechnung, adminKiAbrechnungMonths, adminKiAbrechnungCsvUrl, adminGetKiBudget, adminSetKiBudget } from '../services/api';
import { Robot, CurrencyEur, Download, Calendar, ChartLine, Warning, Receipt, ShieldWarning, CheckCircle, FloppyDisk, Lock } from '@phosphor-icons/react';

const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function formatEur(v) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 4 }).format(v || 0);
}

function formatNum(v) {
  return new Intl.NumberFormat('de-DE').format(v || 0);
}

export default function SuperAdminKIAbrechnung() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [resData, resMonths] = await Promise.all([
        adminKiAbrechnung(year, month),
        adminKiAbrechnungMonths(),
      ]);
      setData(resData.data);
      // Always include current month so user can navigate forward
      const months = [...resMonths.data];
      const hasCurrent = months.some(m => m.year === now.getFullYear() && m.month === now.getMonth() + 1);
      if (!hasCurrent) {
        months.unshift({ year: now.getFullYear(), month: now.getMonth() + 1 });
      }
      setAvailableMonths(months);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const selectKey = `${year}-${month}`;

  return (
    <div className="space-y-6" data-testid="sa-ki-abrechnung-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            KI-Abrechnung
          </h1>
          <p className="text-slate-500 mt-1 text-sm flex items-center gap-1.5">
            <Robot weight="bold" className="w-4 h-4" />
            Kosten pro Mandant für IONOS AI Model Hub
          </p>
        </div>
        <a
          href={adminKiAbrechnungCsvUrl(year, month)}
          target="_blank"
          rel="noreferrer"
          className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 flex items-center justify-center gap-2"
          data-testid="ki-csv-download"
        >
          <Download weight="bold" className="w-4 h-4" />
          CSV exportieren
        </a>
      </div>

      {/* Month selector */}
      <div className="bg-white border border-gray-200 p-4 flex items-center gap-3 flex-wrap">
        <Calendar weight="bold" className="w-5 h-5 text-slate-400" />
        <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Abrechnungsmonat</span>
        <select
          value={selectKey}
          onChange={(e) => {
            const [y, m] = e.target.value.split('-').map(Number);
            setYear(y); setMonth(m);
          }}
          className="border-b border-slate-300 bg-transparent py-1 focus:border-slate-900 outline-none font-mono font-bold text-base"
          data-testid="ki-month-select"
        >
          {availableMonths.map(m => (
            <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
              {MONTHS_DE[m.month - 1]} {m.year}
            </option>
          ))}
        </select>
        <span className="text-xs text-slate-500 ml-auto">Archiv: Vorherige Monate auswählen</span>
      </div>

      {/* KI Budget Brake */}
      <KiBudgetCard />

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-700"></div>
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-emerald-50 border border-emerald-200 p-5">
              <p className="text-xs uppercase tracking-widest font-bold text-emerald-700">Gesamt-Kosten</p>
              <p className="font-mono text-3xl sm:text-4xl font-black tracking-tighter text-emerald-700 mt-2" data-testid="ki-total-cost">
                {formatEur(data?.total_kosten_euro)}
              </p>
              <p className="text-xs text-emerald-700 mt-1">{MONTHS_DE[(data?.month || 1) - 1]} {data?.year}</p>
            </div>
            <div className="bg-violet-50 border border-violet-200 p-5">
              <p className="text-xs uppercase tracking-widest font-bold text-violet-700">Anfragen</p>
              <p className="font-mono text-3xl sm:text-4xl font-black tracking-tighter text-violet-700 mt-2">
                {formatNum(data?.total_anfragen)}
              </p>
              <p className="text-xs text-violet-700 mt-1">KI-Calls insgesamt</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-5">
              <p className="text-xs uppercase tracking-widest font-bold text-blue-700">Mandanten aktiv</p>
              <p className="font-mono text-3xl sm:text-4xl font-black tracking-tighter text-blue-700 mt-2">
                {data?.mandanten?.length || 0}
              </p>
              <p className="text-xs text-blue-700 mt-1">mit KI-Nutzung diesen Monat</p>
            </div>
          </div>

          {/* Per-Mandant breakdown */}
          {data?.mandanten?.length > 0 ? (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ki-mandanten-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Mandant</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Anfragen</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Tokens Input</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Tokens Output</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Fehler</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Kosten</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.mandanten.map((m) => (
                    <tr key={m.mandant_id} className="hover:bg-gray-50" data-testid={`mandant-row-${m.mandant_id}`}>
                      <td className="py-3 px-4 font-medium text-slate-900">{m.mandant_name}</td>
                      <td className="py-3 px-4 text-right font-mono">{formatNum(m.anfragen)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{formatNum(m.tokens_input)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{formatNum(m.tokens_output)}</td>
                      <td className="py-3 px-4 text-right">
                        {m.fehler > 0 ? (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 inline-flex items-center gap-1">
                            <Warning weight="bold" className="w-3 h-3" /> {m.fehler}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-emerald-700">{formatEur(m.kosten_euro)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold">
                    <td className="py-3 px-4">Gesamt</td>
                    <td className="py-3 px-4 text-right font-mono">{formatNum(data.total_anfragen)}</td>
                    <td colSpan={3}></td>
                    <td className="py-3 px-4 text-right font-mono text-emerald-700">{formatEur(data.total_kosten_euro)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 p-12 text-center">
              <Receipt weight="light" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Keine KI-Anfragen in diesem Monat</h3>
              <p className="text-slate-500">Für {MONTHS_DE[(data?.month || 1) - 1]} {data?.year} wurden noch keine KI-Anfragen erfasst.</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 p-4 text-xs text-blue-800">
            <p className="font-medium">Preisliste (IONOS AI Model Hub, EUR pro 1 Mio. Tokens):</p>
            <ul className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-1">
              <li>• Llama 3.3 70B: 0,65 € (Input + Output)</li>
              <li>• GPT OSS 120B: 0,95 € (Input + Output)</li>
              <li>• Llama 3.1 8B: 0,10 € (Input + Output)</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function KiBudgetCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminGetKiBudget();
      setStatus(res.data);
      setDraft(res.data?.budget_eur != null ? String(res.data.budget_eur).replace('.', ',') : '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (disable = false) => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      let val = null;
      if (!disable) {
        const num = parseFloat((draft || '').replace(',', '.'));
        if (Number.isNaN(num) || num < 0) {
          setError('Bitte einen gültigen Betrag eingeben (z.B. 50,00)');
          setSaving(false);
          return;
        }
        val = num;
      }
      const res = await adminSetKiBudget(val);
      setStatus(res.data);
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-white border border-slate-200 p-4 text-sm text-slate-400">Lade Budget…</div>;
  }

  const pct = status?.percent_used ?? 0;
  const blocked = !!status?.blocked;
  const enabled = status?.budget_eur != null;
  const barTone = blocked ? 'bg-red-500'
    : pct >= 80 ? 'bg-amber-500'
    : 'bg-emerald-500';
  const cardTone = blocked ? 'border-red-300 bg-red-50'
    : pct >= 80 ? 'border-amber-300 bg-amber-50'
    : 'border-emerald-200 bg-white';

  return (
    <div className={`border rounded-lg ${cardTone}`} data-testid="ki-budget-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <ShieldWarning weight="bold" className={`w-5 h-5 ${blocked ? 'text-red-700' : pct >= 80 ? 'text-amber-700' : 'text-slate-700'}`} />
        <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900">KI-Budget-Bremse (Monat)</h2>
        {enabled ? (
          <span className="text-xs text-slate-500 ml-1">Notbremse aktiv – Anfragen werden gestoppt wenn Budget erreicht.</span>
        ) : (
          <span className="text-xs text-amber-700 ml-1 font-medium">⚠ Budget-Bremse deaktiviert – KI-Kosten sind unbegrenzt.</span>
        )}
        {blocked && <span className="ml-auto inline-flex items-center gap-1 bg-red-700 text-white px-2 py-0.5 text-xs font-bold rounded"><Lock weight="bold" className="w-3 h-3" /> BLOCKIERT</span>}
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Verbraucht (Monat)</p>
            <p className="font-mono text-2xl font-black text-slate-900 mt-1" data-testid="ki-budget-spent">
              {formatEur(status?.spent_eur)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Budget</p>
            <p className="font-mono text-2xl font-black text-slate-900 mt-1" data-testid="ki-budget-limit">
              {enabled ? formatEur(status?.budget_eur) : '∞'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Verbleibend</p>
            <p className="font-mono text-2xl font-black text-slate-900 mt-1">
              {enabled ? formatEur(status?.remaining_eur) : '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Auslastung</p>
            <p className={`font-mono text-2xl font-black mt-1 ${blocked ? 'text-red-700' : pct >= 80 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="ki-budget-pct">
              {enabled ? `${(status?.percent_used ?? 0).toFixed(1).replace('.', ',')}%` : '—'}
            </p>
          </div>
        </div>

        {enabled && (
          <div>
            <div className="h-3 bg-slate-200 rounded overflow-hidden" data-testid="ki-budget-progress">
              <div
                className={`${barTone} h-full transition-all duration-500`}
                style={{ width: `${Math.min(100, pct)}%` }}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Aktueller Verbrauch: <strong>{formatEur(status?.spent_eur)}</strong> von <strong>{formatEur(status?.budget_eur)}</strong>
            </p>
          </div>
        )}

        {/* Edit */}
        {editing ? (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Neues Budget (€)</label>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="50,00"
              className="border border-slate-300 px-3 py-1.5 text-sm font-mono w-32 rounded focus:border-violet-500 outline-none"
              data-testid="ki-budget-input"
            />
            <button
              type="button"
              onClick={() => save(false)}
              disabled={saving}
              className="bg-violet-700 text-white px-3 py-1.5 text-sm font-medium hover:bg-violet-800 disabled:opacity-50 rounded inline-flex items-center gap-1.5"
              data-testid="ki-budget-save-button"
            >
              <FloppyDisk weight="bold" className="w-4 h-4" />
              Speichern
            </button>
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving}
              className="border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 rounded"
              data-testid="ki-budget-disable-button"
            >
              Bremse deaktivieren
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setError(''); load(); }}
              className="text-xs text-slate-500 hover:text-slate-900 ml-1"
            >Abbrechen</button>
          </div>
        ) : (
          <div className="pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-sm text-violet-700 hover:text-violet-900 font-medium"
              data-testid="ki-budget-edit-button"
            >
              Budget bearbeiten →
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm flex items-center gap-2 rounded">
            <Warning weight="bold" className="w-4 h-4" /> {error}
          </div>
        )}
        {saved && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 text-sm flex items-center gap-2 rounded">
            <CheckCircle weight="fill" className="w-4 h-4" /> Budget aktualisiert.
          </div>
        )}
      </div>
    </div>
  );
}

